<template>
  <div
    class="flex flex-col items-baseline justify-end lg:flex-row"
    :class="{
      'mb-6': !EXCLUDE_ORG_TITLE_TABS.includes(activeTab),
    }"
  >
    <div
      v-if="$t('ORGANIZATION.SALES_REPRESENTATIVES') === activeTab"
      class="user-controls-wrapper"
    >
      <search-input v-model="search" class="mr-2 -mb-0 -ml-2 lg:-ml-0" />
      <access-control :roles="[SUPER_ADMIN, ADMIN, CLIENT_ADMIN]">
        <primary-button
          class="h-full"
          @click.prevent="showSalesRepModal = true"
        >
          {{ $t("ORGANIZATION.ADD_SALES_REP") }}
        </primary-button>
      </access-control>
    </div>
  </div>
  <div>
    <component
      :is="organizationTabs[activeTab]"
      v-if="showComponent"
      :search="search"
      :client-id="activeClient?.id"
      :client-notifications="activeClient?.notifications || []"
      :company-name="activeClient?.name"
      :user-type="UserType.client"
      :class="{
        'max-w-(--breakpoint-md)': activeTab === $t('NOTIFICATIONS.TITLE'),
      }"
    />
    <loader v-else is-loading />
  </div>
  <sales-rep-modal
    v-if="showSalesRepModal"
    :close="() => (showSalesRepModal = false)"
  />
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { Ability, PermissionSubject, UserType } from "@/enums/auth";
import { ADMIN, CLIENT_ADMIN, SUPER_ADMIN } from "@/helpers/constants";
import { EMAIL_INTEGRATION_HASH } from "@/helpers/constants/emails";
import { ProfileViewActiveTab } from "@/helpers/symbols";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/auth";

import PresetsList from "@/components/presets/PresetsList.vue";
import Inquiries from "@/components/profile/Inquiries.vue";
import CountriesView from "@/views/profile/CountriesView.vue";
import WebhookNotificationsViewVue from "@/views/profile/WebhookNotificationsView.vue";
import ApiConfig from "@/views/profile/components/ApiConfig.vue";
import BrandName from "@/views/profile/components/BrandName.vue";
import BusinessCreditData from "@/views/profile/components/BusinessCreditData.vue";
import CloseApi from "@/views/profile/components/CloseApi.vue";
import Commissions from "@/views/profile/components/Commissions.vue";
import FeatureAccessView from "@/views/profile/components/FeatureAccessView.vue";
import LenderNetworkSettings from "@/views/profile/components/LenderNetworkSettings.vue";
import PasswordSettings from "@/views/profile/components/PasswordSettings.vue";
import SSNMask from "@/views/profile/components/SSNMask.vue";
import SalesRepresentative from "@/views/profile/components/SalesRepresentatives.vue";
import TestMode from "@/views/profile/components/TestMode.vue";
import EmployeesList from "@/views/profile/components/UsersList.vue";
import NotificationsSettings from "@/views/profile/components/notifications/NotificationsSettings.vue";
import EmailIntegration from "@/views/profile/emailIntegration/EmailIntegrationWhitelabels.vue";
import SalesRepModal from "@/views/profile/modals/SalesRepModal.vue";

import type { IClient } from "@/models/clients";

const { canPerformActionReactive, EXCLUDE_ORG_TITLE_TABS } = usePermissions();
const route = useRoute();
const store = useStore();
const { t } = useI18n();
const auth = useAuth();

const showSalesRepModal = ref(false);
const search = ref("");
const activeClient = computed<IClient | null>(
  () => store.getters["clients/active"]
);

const organizationTabs = computed(() => ({
  [t("ORGANIZATION.USER_MANAGEMENT")]: EmployeesList,
  [t("ORGANIZATION.COMMISSIONS.TITLE")]: Commissions,
  [t("ORGANIZATION.BUSINESS_CREDIT_DATA.TITLE")]: BusinessCreditData,
  [t("ORGANIZATION.FEATURE_ACCESS.TITLE")]: FeatureAccessView,
  [t("ORGANIZATION.API_CONFIG.TITLE")]: ApiConfig,
  [t("ORGANIZATION.PASSWORD_SETTINGS.TAB_TITLE")]: PasswordSettings,
  [t("NOTIFICATIONS.EMAIL_NOTIFICATIONS")]:
    !auth.isClient && NotificationsSettings,
  [t("NOTIFICATIONS.WEBHOOK_NOTIFICATIONS")]:
    !auth.isClient && WebhookNotificationsViewVue,
  [t("ORGANIZATION.CLOSE_API.TAB_NAME")]: !auth.isClient && CloseApi,
  [t("ORGANIZATION.SALES_REPRESENTATIVES")]:
    !!activeClient.value?.equipment_rental_enabled && SalesRepresentative,
  [t("EMAIL_INTEGRATION.TITLE")]:
    (auth.isAdmin || auth.isEmployee) && EmailIntegration,
  [t("COMMON.COUNTRIES")]:
    (auth.isAdmin || auth.isSuperAdmin || auth.isUnderwriter) && CountriesView,
  [t("ORGANIZATION.TEST_MODE.TITLE")]:
    (auth.isAdmin || auth.isSuperAdmin) && TestMode,
  [t("ORGANIZATION.INQUIRIES.TITLE")]: Inquiries,
  [t("ORGANIZATION.SSN_MASK.TITLE")]:
    (auth.isAdmin || auth.isSuperAdmin || auth.isClientAdmin) && SSNMask,
  [t("PRESETS.TITLE")]: canViewPresets.value && PresetsList,
  [t("LENDER_NETWORK.TITLE")]: LenderNetworkSettings,
  [`${t("INTEGRATIONS.BRAND")} ${t("COMMON.NAME")}`]: BrandName,
}));

const activeTab = inject(
  ProfileViewActiveTab,
  ref(t("ORGANIZATION.USER_MANAGEMENT"))
);

const canViewPresets = canPerformActionReactive(
  PermissionSubject.permissionsPreset,
  Ability.viewAny,
  {}
);

const authClientSettings = computed<IClient>(
  () => store.getters["auth/authClientSettings"]
);

const showComponent = computed(() => {
  const isEmailNotifications =
    activeTab.value === t("NOTIFICATIONS.EMAIL_NOTIFICATIONS");

  const canShowEmailNotifications =
    activeClient.value?.id &&
    activeClient.value?.name &&
    activeClient.value?.notifications;

  return (
    !isEmailNotifications || (isEmailNotifications && canShowEmailNotifications)
  );
});

onBeforeMount(() => {
  if (route.hash === EMAIL_INTEGRATION_HASH) {
    activeTab.value = t("EMAIL_INTEGRATION.TITLE");
  }
  store.dispatch(
    "clients/find",
    auth.isClient ? authClientSettings.value.id : route.params.id
  );
});

onUnmounted(() => {
  store.commit("clients/unsetActive");
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.user-controls-wrapper {
  @apply mt-4 flex w-full flex-col justify-center md:flex-row lg:mt-0 lg:w-max;
}
</style>
